import { type ReactNode } from "react";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import { InfoIcon } from "@/components/Icons/InfoIcon";
import { cn } from "@/lib/utils";
import { formatPrice } from "../SummaryPrice";
import {
  getCreditsPaymentBreakdown,
  type CreditsTopUpPaymentMethod
} from "../VerticalFlowComponents/creditsPaymentAmount";

function LabelWithTooltip({
  label,
  tooltipId,
  tooltip
}: {
  label: string;
  tooltipId: string;
  tooltip: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{label}</span>
      <SimpleTooltip
        id={tooltipId}
        content={tooltip}
        place="top"
        usePortal
        tooltipClassName="!max-w-[300px] whitespace-normal"
      >
        <InfoIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </SimpleTooltip>
    </span>
  );
}

function BreakdownRow({
  label,
  value,
  indented = false,
  strong = false,
  tooltipId,
  tooltip,
  className
}: {
  label: ReactNode;
  value: ReactNode;
  indented?: boolean;
  strong?: boolean;
  tooltipId?: string;
  tooltip?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex w-full items-center justify-between gap-6 py-2.5",
        indented ? "pl-3" : undefined,
        strong ? "text-foreground" : "text-muted-foreground",
        className
      )}
    >
      <div
        className={cn(
          "min-w-0 text-left text-[13px] leading-5",
          strong && "text-sm font-medium text-foreground"
        )}
      >
        {typeof label === "string" && tooltipId && tooltip ? (
          <LabelWithTooltip label={label} tooltipId={tooltipId} tooltip={tooltip} />
        ) : (
          label
        )}
      </div>
      <div
        className={cn(
          "shrink-0 text-right text-[13px] tabular-nums leading-5",
          strong ? "text-sm font-semibold text-foreground" : "font-medium text-foreground"
        )}
      >
        {value}
      </div>
    </div>
  );
}

function BreakdownSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex w-full flex-col">
      <p className="m-0 border-b border-border px-4 pb-2 pt-3 text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
        {title}
      </p>
      <div className="flex flex-col px-4">{children}</div>
    </div>
  );
}

export type CreditsPaymentBreakdownProps = {
  /** Total booking cost. */
  amount: number;
  /** Total spendable credits (`credits_balance`). */
  creditBalance: number;
  /** Paid (withdrawable) credits. */
  purchasedBalance: number;
  /** Courtesy (non-withdrawable, floor-exempt) credits. */
  courtesyBalance?: number;
  /** Required paid-credits floor when paying with credits; 0 if none. */
  minNonPromotionalCreditsRequired?: number;
  /** Top-up method — only affects the card-minimum surplus line; omit when there is no top-up. */
  topUpPaymentMethod?: CreditsTopUpPaymentMethod;
  /** Labels the total as an estimate for pay-as-you-go deployments. */
  isPayg?: boolean;
  className?: string;
};

/**
 * Read-only receipt of how a credits payment is split: total cost, courtesy → promo → paid
 * drawn from the existing balance, and any additional paid credits still required. Shared by
 * the deploy confirm modal and the extend-rental form so both surfaces stay identical.
 */
export function CreditsPaymentBreakdown({
  amount,
  creditBalance,
  purchasedBalance,
  courtesyBalance = 0,
  minNonPromotionalCreditsRequired = 0,
  topUpPaymentMethod,
  isPayg = false,
  className
}: CreditsPaymentBreakdownProps) {
  const {
    totalCost,
    courtesyApplied,
    promoApplied,
    purchasedApplied,
    additionalRequired,
    stripeMinimumSurplus
  } = getCreditsPaymentBreakdown({
    totalCost: amount,
    creditsBalance: creditBalance,
    purchasedCreditsBalance: purchasedBalance,
    minNonPromotionalCreditsRequired,
    courtesyCreditsBalance: courtesyBalance,
    topUpPaymentMethod
  });

  const formattedTotal = formatPrice("credits", totalCost, 2);
  const formattedCourtesy = formatPrice("credits", courtesyApplied, 2);
  const formattedPromo = formatPrice("credits", promoApplied, 2);
  const formattedPurchased = formatPrice("credits", purchasedApplied, 2);
  const formattedAdditional = formatPrice("credits", additionalRequired, 2);
  const formattedStripeSurplus = formatPrice("credits", stripeMinimumSurplus, 2);

  const courtesyTooltip =
    "Courtesy credits applied to this booking. They are drawn down first and are exempt from the paid-credit requirement.";
  const promoTooltip =
    "Promotional credits applied to this booking. Promotional credits can cover up to 30% of the total booking cost.";
  const purchasedTooltip = "Paid credits from your balance that will be applied to this booking.";
  const additionalTooltip = `Additional paid credits needed to complete this booking because promotional credits can cover only up to 30% of the booking cost. You’ll need to purchase an additional ${formattedAdditional} in paid credits.`;
  const stripeMinTooltip = `Credit card top-ups have a $10 minimum. Since only ${formattedAdditional} is needed for this booking, the remaining ${formattedStripeSurplus} will be added to your credit balance for future use.`;

  return (
    <div className={cn("overflow-hidden rounded-lg border border-border bg-muted/20", className)}>
      <div className="border-b border-border bg-background px-4 py-3.5">
        <BreakdownRow
          label={isPayg ? "Estimated credits needed" : "Total booking cost"}
          value={formattedTotal}
          strong
          className="py-0"
        />
      </div>

      <div className="flex flex-col divide-y divide-border">
        <BreakdownSection title="Existing credits balance">
          {courtesyApplied > 0 && (
            <BreakdownRow
              indented
              label="Courtesy credits"
              tooltipId="credits-confirm-courtesy-tooltip"
              tooltip={courtesyTooltip}
              value={formattedCourtesy}
            />
          )}
          <BreakdownRow
            indented
            label="Promotional credits"
            tooltipId="credits-confirm-promo-tooltip"
            tooltip={promoTooltip}
            value={formattedPromo}
          />
          <BreakdownRow
            indented
            label="Paid credits"
            tooltipId="credits-confirm-purchased-tooltip"
            tooltip={purchasedTooltip}
            value={formattedPurchased}
          />
        </BreakdownSection>

        {additionalRequired > 0 && (
          <BreakdownSection title="Additional credits required">
            <BreakdownRow
              indented
              label="Paid credits required"
              tooltipId="credits-confirm-additional-tooltip"
              tooltip={additionalTooltip}
              value={formattedAdditional}
            />
            {stripeMinimumSurplus > 0 && (
              <BreakdownRow
                indented
                label="Credit card minimum top-up amount"
                tooltipId="credits-confirm-stripe-min-tooltip"
                tooltip={stripeMinTooltip}
                value={formattedStripeSurplus}
              />
            )}
          </BreakdownSection>
        )}
      </div>
    </div>
  );
}
